import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import { FC, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { Autocomplete, Box, DialogContent, TextField, Typography } from '@mui/material';
import { createSwissPostRequest } from 'redux/reducers/swissPost/reducer';
import { useDispatch, useSelector } from 'react-redux';
import { getSwissPostAvailableEms } from 'redux/reducers/swissPost/selectors';
import { PAGE_SIZE } from 'services/constants/env';
import queryString from 'query-string';
import { useLocation } from 'react-router-dom';
import { TConnectClientDialogProps, TConnectClientValues } from './types';
import { validationSchema } from './validationSchema';

export const ConnectClientDialog: FC<TConnectClientDialogProps> = ({
	open,
	setClose,
	data,
	isEdit = false,
}) => {
	const { search } = useLocation();
	const navigateParams = queryString.parse(search);
	const dispatch = useDispatch();
	const [value, setValue] = useState<string>('');
	const [valueIndex, setValueIndex] = useState<number | null>(null);
	const [address, setAddress] = useState<string>('');
	const [city, setCity] = useState<string>('');
	const [iossProjectId, setIossProjectId] = useState<string>('');
	const [ddpProjectId, setDdpProjectId] = useState<string>('');
	const [zipCode, setZipCode] = useState<string>('');
	const [countryIso2Code, setCountryIso2Code] = useState<string>('');
	const [defaultHs6Code, setDefaultHs6Code] = useState<string>('');
	const [frankingLicense, setFrankingLicense] = useState<string>('');
	const [swissFirstMileClientId, setSwissFirstMileClientId] = useState<string>('');
	const [swissFirstMileClientSecret, setSwissFirstMileClientSecret] = useState<string>('');
	const availableEms = useSelector(getSwissPostAvailableEms);

	const reset = () => {
		setValue('');
		setValueIndex(null);
		setAddress('');
		setCity('');
		setIossProjectId('');
		setDdpProjectId('');
		setZipCode('');
		setCountryIso2Code('');
		setDefaultHs6Code('');
		setFrankingLicense('');
		setSwissFirstMileClientId('');
		setSwissFirstMileClientSecret('');
	};

	const handleClickConnect = (values: TConnectClientValues) => {
		let emId;
		if (data) emId = data.em_id;
		else if (value && valueIndex !== null && availableEms) emId = availableEms[valueIndex].id;
		else return;
		dispatch(
			createSwissPostRequest({
				body: {
					emId,
					address: values.address,
					city: values.city,
					zipCode: values.zipCode,
					msgProjectIdOss: +values.ddpProjectId,
					msgProjectIdIoss: +values.iossProjectId,
					defaultHs6Code: values.defaultHs6Code,
					countryIso2Code,
					isEnabled: data?.is_enabled ?? true,
					frankingLicense: values.frankingLicense,
					swissFirstMileClientId: values.swissFirstMileClientId,
					swissFirstMileClientSecret: values.swissFirstMileClientSecret,
				},
				request: {
					pageNumber: 1,
					pageSize: PAGE_SIZE,
					searchString: String(navigateParams?.search ?? ''),
				},
			}),
		);
	};

	const formik = useFormik({
		initialValues: {
			address,
			city,
			zipCode,
			iossProjectId,
			ddpProjectId,
			defaultHs6Code,
			frankingLicense,
			swissFirstMileClientId,
			swissFirstMileClientSecret,
		},
		enableReinitialize: true,
		validationSchema,
		onSubmit: values => {
			handleClickConnect(values);
			formik.resetForm();
			reset();
			setClose();
		},
	});

	useEffect(() => {
		if (!open) return;
		if (data) {
			setAddress(data.shipper_address);
			setCity(data.shipper_city);
			setIossProjectId(data.msg_project_id_ioss ? String(data.msg_project_id_ioss) : '');
			setDdpProjectId(data.msg_project_id_oss ? String(data.msg_project_id_oss) : '');
			setZipCode(data.shipper_zip_code);
			setCountryIso2Code(data.shipper_country_iso_code);
			setDefaultHs6Code(data.default_hs6_code);
			setFrankingLicense(data.franking_license);
			setSwissFirstMileClientId(data.swiss_first_mile_client_id);
			setSwissFirstMileClientSecret(data.swiss_first_mile_client_secret);
			return;
		}
		if (!availableEms) return;
		if (valueIndex === null) return;
		if (!availableEms[valueIndex]) return;
		if (availableEms[valueIndex]?.zip_code) {
			setZipCode(availableEms[valueIndex]?.zip_code as string);
			formik.handleChange({
				target: { name: 'zipCode', value: availableEms[valueIndex]?.zip_code as string },
			});
		}
		if (availableEms[valueIndex]?.country_code)
			setCountryIso2Code(availableEms[valueIndex]?.country_code as string);
		if (availableEms[valueIndex]?.city) setCity(availableEms[valueIndex]?.city as string);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [valueIndex, open, availableEms]);

	return (
		<Dialog
			open={open}
			aria-labelledby="alert-dialog-title"
			aria-describedby="alert-dialog-description"
		>
			<DialogTitle color="primary">{isEdit ? 'Edit' : 'Connect'} Client to Swiss Post</DialogTitle>
			<form onSubmit={formik.handleSubmit}>
				<DialogContent>
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							gap: 2,
							width: '500px',
							minHeight: '470px',
							padding: '10px 0 0 0',
						}}
					>
						{!isEdit && !data && !!availableEms && availableEms.length > 0 && (
							<Autocomplete
								value={value}
								onChange={(event: any, newValue: string | null) => {
									reset();
									setValue(newValue ?? '');
									const index = availableEms.findIndex(item => item.full_name === newValue);
									setValueIndex(index);
									formik.resetForm();
								}}
								options={availableEms.map(option => option.full_name)}
								renderInput={params => <TextField {...params} label="Client" />}
								fullWidth
								sx={{ width: '100%' }}
								size="small"
							/>
						)}
						{(data || (!!availableEms && valueIndex !== null)) && (
							<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
								{!!availableEms && valueIndex !== null && (
									<Typography variant="h6">{availableEms[valueIndex]?.address ?? ''}</Typography>
								)}
								<TextField
									id="address"
									name="address"
									label="Address without zip code, city and country"
									fullWidth
									value={formik.values.address}
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									error={formik.touched.address && Boolean(formik.errors.address)}
									helperText={formik.touched.address && formik.errors.address}
									size="small"
								/>
								<TextField
									id="city"
									name="city"
									label="City"
									fullWidth
									value={formik.values.city}
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									error={formik.touched.city && Boolean(formik.errors.city)}
									helperText={formik.touched.city && formik.errors.city}
									size="small"
								/>
								<TextField
									id="zipCode"
									name="zipCode"
									label="Zip Code"
									fullWidth
									value={formik.values.zipCode}
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									error={formik.touched.zipCode && Boolean(formik.errors.zipCode)}
									helperText={formik.touched.zipCode && formik.errors.zipCode}
									size="small"
								/>
								<TextField
									id="iossProjectId"
									name="iossProjectId"
									label="IOSS Project ID"
									fullWidth
									value={formik.values.iossProjectId}
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									error={formik.touched.iossProjectId && Boolean(formik.errors.iossProjectId)}
									helperText={formik.touched.iossProjectId && formik.errors.iossProjectId}
									size="small"
								/>
								<TextField
									id="ddpProjectId"
									name="ddpProjectId"
									label="DDP Project ID"
									fullWidth
									value={formik.values.ddpProjectId}
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									error={formik.touched.ddpProjectId && Boolean(formik.errors.ddpProjectId)}
									helperText={formik.touched.ddpProjectId && formik.errors.ddpProjectId}
									size="small"
								/>
								<TextField
									id="defaultHs6Code"
									name="defaultHs6Code"
									label="Default HS code to be used"
									fullWidth
									value={formik.values.defaultHs6Code}
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									error={formik.touched.defaultHs6Code && Boolean(formik.errors.defaultHs6Code)}
									helperText={formik.touched.defaultHs6Code && formik.errors.defaultHs6Code}
									size="small"
								/>
								<TextField
									id="frankingLicense"
									name="frankingLicense"
									label="Franking License"
									fullWidth
									value={formik.values.frankingLicense}
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									error={formik.touched.frankingLicense && Boolean(formik.errors.frankingLicense)}
									helperText={formik.touched.frankingLicense && formik.errors.frankingLicense}
									size="small"
								/>
								<TextField
									id="swissFirstMileClientId"
									name="swissFirstMileClientId"
									label="Swiss First Mile Client ID"
									fullWidth
									value={formik.values.swissFirstMileClientId}
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									error={
										formik.touched.swissFirstMileClientId &&
										Boolean(formik.errors.swissFirstMileClientId)
									}
									helperText={
										formik.touched.swissFirstMileClientId && formik.errors.swissFirstMileClientId
									}
									size="small"
								/>
								<TextField
									id="swissFirstMileClientSecret"
									name="swissFirstMileClientSecret"
									label="Swiss First Mile Client Secret"
									fullWidth
									value={formik.values.swissFirstMileClientSecret}
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									error={
										formik.touched.swissFirstMileClientSecret &&
										Boolean(formik.errors.swissFirstMileClientSecret)
									}
									helperText={
										formik.touched.swissFirstMileClientSecret &&
										formik.errors.swissFirstMileClientSecret
									}
									size="small"
								/>
								{/* <CountrySelectMenu
								onChange={setCountryIso2Code}
								countryCode={countryIso2Code}
								size="medium"
							/> */}
							</Box>
						)}
					</Box>
				</DialogContent>
				<DialogActions>
					<Button
						type="button"
						onClick={() => {
							formik.resetForm();
							reset();
							setClose();
						}}
					>
						Cancel
					</Button>
					<Button
						disabled={
							!data &&
							(!value ||
								!countryIso2Code ||
								valueIndex === null ||
								!availableEms ||
								!availableEms[valueIndex])
						}
						type="submit"
					>
						{data ? 'Edit' : 'Connect'}
					</Button>
				</DialogActions>
			</form>
		</Dialog>
	);
};
